import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Logo from "../components/Logo";
import ThemeToggle from "../components/ThemeToggle";
import { Lock, LogOut, Plus, Trash2, ArrowLeft, ArrowRight, Save, Loader2, FileText, Tag as TagIcon } from "lucide-react";
import { toast } from "../hooks/use-toast";
import { useAdminSession } from "../hooks/use-admin-session";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const createId = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
const createListItem = (text = "") => ({ id: createId(), text });
const createBlock = (type = "p") => (type === "ul" ? { id: createId(), type, items: [createListItem()] } : { id: createId(), type, text: "" });

const emptyForm = () => ({
  slug: "",
  title: "",
  excerpt: "",
  tags: "",
  date: new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
  read_time: "5 min read",
  cover: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1600&q=80&auto=format&fit=crop",
  blocks: [createBlock("p")],
});

const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80);

const BlogEditor = () => {
  const { authenticated, checkingSession, authLoading: signInLoading, signIn, signOut, adminSessionConfig } = useAdminSession();
  const [inputToken, setInputToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(emptyForm());

  const handleSignIn = async (e) => {
    e.preventDefault();
    const ok = await signIn(inputToken);
    if (ok) {
      setInputToken("");
    }
  };

  const logout = useCallback(async () => {
    await signOut();
    setInputToken("");
    setPosts([]);
  }, [signOut]);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/blog/posts`);
      setPosts(r.data);
    } catch {
      toast({ title: "Failed to load posts" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authenticated) fetchPosts();
  }, [authenticated, fetchPosts]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setBlock = (blockId, patch) => setForm((f) => ({ ...f, blocks: f.blocks.map((b) => (b.id === blockId ? { ...b, ...patch } : b)) }));
  const addBlock = (type) => setForm((f) => ({ ...f, blocks: [...f.blocks, createBlock(type)] }));
  const removeBlock = (blockId) => setForm((f) => ({ ...f, blocks: f.blocks.filter((b) => b.id !== blockId) }));
  const setListItem = (blockId, itemId, val) => setForm((f) => ({
    ...f,
    blocks: f.blocks.map((b) => (b.id === blockId ? { ...b, items: b.items.map((item) => (item.id === itemId ? { ...item, text: val } : item)) } : b)),
  }));
  const addListItem = (blockId) => setForm((f) => ({
    ...f,
    blocks: f.blocks.map((b) => (b.id === blockId ? { ...b, items: [...(b.items || []), createListItem()] } : b)),
  }));

  const publish = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.excerpt.trim() || !form.slug.trim()) {
      toast({ title: "Missing fields", description: "Title, excerpt and slug are required." });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        slug: slugify(form.slug),
        title: form.title.trim(),
        excerpt: form.excerpt.trim(),
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        date: form.date,
        read_time: form.read_time,
        cover: form.cover,
        content: form.blocks
          .map((b) => {
            if (b.type === "ul") return { type: "ul", items: (b.items || []).map((item) => item.text).filter((x) => x.trim().length) };
            return { type: b.type, text: (b.text || "").trim() };
          })
          .filter((b) => (b.type === "ul" ? (b.items || []).length : (b.text || "").length)),
      };
      await axios.post(`${API}/blog/posts`, payload, adminSessionConfig);
      toast({ title: "Published!", description: "Article is now live." });
      setForm(emptyForm());
      fetchPosts();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to publish.";
      toast({ title: "Publish failed", description: typeof detail === "string" ? detail : "Please check your inputs." });
      if (err?.response?.status === 401) await logout();
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (slug) => {
    if (!confirm(`Delete post "${slug}"?`)) return;
    try {
      await axios.delete(`${API}/blog/posts/${slug}`, adminSessionConfig);
      toast({ title: "Deleted" });
      fetchPosts();
    } catch (err) {
      toast({ title: "Delete failed" });
      if (err?.response?.status === 401) await logout();
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F7FB] text-[#0B1B3B]">
        <div className="inline-flex items-center gap-3 text-sm font-semibold"><Loader2 size={18} className="animate-spin" /> Checking admin session…</div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F7FB] p-6">
        <form onSubmit={handleSignIn} className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8 shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <Logo variant="light" size={40} />
            <ThemeToggle />
          </div>
          <div className="h-12 w-12 rounded-xl bg-[#0B1B3B] text-white flex items-center justify-center"><Lock size={20} /></div>
          <h1 className="mt-4 text-2xl font-bold text-[#0B1B3B]">Blog editor sign-in</h1>
          <p className="mt-2 text-sm text-slate-600">Enter your admin token to start a secure cookie session for publishing articles.</p>
          <label className="block mt-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Admin Token</label>
          <input
            type="password"
            value={inputToken}
            onChange={(e) => setInputToken(e.target.value)}
            placeholder="Paste your token…"
            className="mt-2 w-full border border-slate-200 rounded-lg px-4 py-3 outline-none focus:border-[#1E56E3] text-sm"
          />
          <button type="submit" disabled={signInLoading} className="btn-primary mt-5 w-full justify-center disabled:opacity-70">
            {signInLoading ? (<><Loader2 size={16} className="animate-spin" /> Verifying…</>) : ("Sign In")}
          </button>
          <div className="mt-6 text-center">
            <Link to="/" className="text-xs text-slate-500 hover:text-[#1E56E3] inline-flex items-center gap-1"><ArrowLeft size={12} /> Back to site</Link>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      <div className="bg-white border-b border-slate-200">
        <div className="container-x flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Logo variant="light" size={36} />
            <span className="hidden md:inline text-xs font-semibold uppercase tracking-wider text-slate-500 border border-slate-200 rounded-full px-3 py-1">Blog Editor</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/insights" className="text-sm font-semibold text-[#0B1B3B] hover:text-[#1E56E3] px-3 py-2">View blog</Link>
            <ThemeToggle />
            <button onClick={logout} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B1B3B] hover:text-[#1E56E3] px-3 py-2">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="container-x py-8 grid lg:grid-cols-12 gap-6">
        {/* Editor form */}
        <form onSubmit={publish} className="lg:col-span-8 bg-white rounded-2xl border border-slate-200 p-8 space-y-5">
          <div className="flex items-center gap-2"><FileText size={16} className="text-[#1E56E3]" /><h1 className="text-2xl font-bold text-[#0B1B3B]">New article</h1></div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Title*</label>
            <input value={form.title} onChange={(e) => { setField("title", e.target.value); if (!form.slug) setField("slug", slugify(e.target.value)); }} required className="mt-1 w-full border-b border-slate-300 py-3 outline-none focus:border-[#1E56E3] text-lg font-semibold bg-transparent" placeholder="e.g., dbt on Microsoft Fabric Warehouse in 2026" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Slug*</label>
              <input value={form.slug} onChange={(e) => setField("slug", slugify(e.target.value))} required className="mt-1 w-full border-b border-slate-300 py-3 outline-none focus:border-[#1E56E3] text-sm bg-transparent" placeholder="my-article-slug" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tags (comma-separated)</label>
              <input value={form.tags} onChange={(e) => setField("tags", e.target.value)} className="mt-1 w-full border-b border-slate-300 py-3 outline-none focus:border-[#1E56E3] text-sm bg-transparent" placeholder="Databricks, dbt, Governance" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date</label>
              <input value={form.date} onChange={(e) => setField("date", e.target.value)} className="mt-1 w-full border-b border-slate-300 py-3 outline-none focus:border-[#1E56E3] text-sm bg-transparent" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Read time</label>
              <input value={form.read_time} onChange={(e) => setField("read_time", e.target.value)} className="mt-1 w-full border-b border-slate-300 py-3 outline-none focus:border-[#1E56E3] text-sm bg-transparent" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cover image URL</label>
            <input value={form.cover} onChange={(e) => setField("cover", e.target.value)} className="mt-1 w-full border-b border-slate-300 py-3 outline-none focus:border-[#1E56E3] text-sm bg-transparent" placeholder="https://…" />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Excerpt*</label>
            <textarea value={form.excerpt} onChange={(e) => setField("excerpt", e.target.value)} required rows={3} className="mt-1 w-full border border-slate-200 rounded-lg px-4 py-3 outline-none focus:border-[#1E56E3] text-sm resize-none" placeholder="One-paragraph summary shown on the blog index…" />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Content blocks</label>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => addBlock("h")} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 hover:border-[#1E56E3] hover:text-[#1E56E3]">+ Heading</button>
                <button type="button" onClick={() => addBlock("p")} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 hover:border-[#1E56E3] hover:text-[#1E56E3]">+ Paragraph</button>
                <button type="button" onClick={() => addBlock("ul")} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 hover:border-[#1E56E3] hover:text-[#1E56E3]">+ Bullet list</button>
              </div>
            </div>

            <div className="mt-4 space-y-4">
              {form.blocks.map((b) => (
                <div key={b.id} className="relative rounded-xl border border-slate-200 p-4 bg-[#FAFBFF]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#1E56E3]">
                      {b.type === "h" ? "Heading" : b.type === "ul" ? "Bullet list" : "Paragraph"}
                    </span>
                    <button type="button" onClick={() => removeBlock(b.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                  </div>
                  {b.type !== "ul" ? (
                    <textarea
                      value={b.text || ""}
                      onChange={(e) => setBlock(b.id, { text: e.target.value })}
                      rows={b.type === "h" ? 1 : 3}
                      className={`w-full border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-[#1E56E3] resize-none ${b.type === "h" ? "text-lg font-bold" : "text-sm"}`}
                      placeholder={b.type === "h" ? "Section heading…" : "Write your paragraph…"}
                    />
                  ) : (
                    <div className="space-y-2">
                      {(b.items || []).map((it) => (
                        <input key={it.id} value={it.text} onChange={(e) => setListItem(b.id, it.id, e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-[#1E56E3] text-sm" placeholder="List item…" />
                      ))}
                      <button type="button" onClick={() => addListItem(b.id)} className="text-xs font-semibold text-[#1E56E3] inline-flex items-center gap-1"><Plus size={12} /> Add item</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <button type="submit" disabled={saving} className="btn-primary disabled:opacity-70">
            {saving ? (<><Loader2 size={16} className="animate-spin" /> Publishing…</>) : (<><Save size={16} /> Publish Article <ArrowRight size={16} /></>)}
          </button>
        </form>

        {/* Existing posts */}
        <aside className="lg:col-span-4 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Published articles</h3>
            <div className="mt-4 space-y-2 max-h-[600px] overflow-y-auto pr-1">
              {loading && <div className="text-sm text-slate-500">Loading…</div>}
              {!loading && posts.length === 0 && <div className="text-sm text-slate-500">No dynamic posts yet. Publish one above.</div>}
              {posts.map((p) => (
                <div key={p.slug} className="rounded-lg border border-slate-200 p-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#0B1B3B] line-clamp-1">{p.title}</div>
                    <div className="text-[11px] text-slate-500 line-clamp-1">{p.slug}</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(p.tags || []).slice(0, 3).map((t) => (
                        <span key={t} className="text-[10px] font-medium bg-[#EEF3FF] text-[#1E56E3] px-2 py-0.5 rounded-full inline-flex items-center gap-1"><TagIcon size={9} />{t}</span>
                      ))}
                    </div>
                  </div>
                  <button onClick={() => deletePost(p.slug)} className="h-8 w-8 rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-200 flex items-center justify-center"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default BlogEditor;
